import React, { Component } from 'react';
import { Modal, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import * as Cookies from 'es-cookie';
import { ADM_MODAL_HEADER, ADM_MODAL_TITLE, ADM_MODAL_TITLE_SUB, ADM_MODAL_BODY, ADM_MODAL_FOOTER, ADM_MODAL_HEADER_DOT } from '../admissionForms/admissionModalStyles';

const THEME = {
  primary: "#6366f1",
  success: "#10b981",
  text: "#0f172a",
  textMuted: "#64748b",
  border: "#e2e8f0",
  bgLight: "#f8fafc",
};

const BASE_FIELDS = [
  { key: 'student_name', label: "Student Name" },
  { key: 'gender', label: "Gender" },
  { key: 'dob', label: "Date of Birth" },
  { key: 'father_name', label: "Father Name" },
    { key: 'father_phone', label: "Father phone" },
  { key: 'mother_name', label: "Mother Name" },
    { key: 'mother_phone', label: "Mother Phone" },
  { key: 'adhar_no', label: "Aadhaar Number", optional: true },
  { key: 'pen_no', label: "PEN Number", optional: true },

];

// student_mobile is no longer collected as a basic input. Any mobile field that a
// form was built with is recognized here so it's neither rendered nor submitted as a
// custom answer — its value is derived from the parent phone when storing.
const isMobileBaseField = (f) => {
  if (!f) return false;
  if (f.field_key === 'student_mobile') return true;
  const n = String(f.field_name || '').toLowerCase().trim().replace(/_/g, ' ');
  return (
    n === 'mobile' ||
    n === 'mobile number' ||
    n === 'student mobile' ||
    n === 'primary contact number' ||
    n === 'contact number'
  );
};




const GET_INITIAL_DATA = gql`
  query($institution_id: Int!, $branch_id_str: String) {
    getExamClasses {
      id
      class
    }
    getAcademicYears {
      id
      academic_year
    }
    getBranches(institution_id: $institution_id, branch_id: $branch_id_str) {
      id
      branch_name
      sections {
        id
        section_name
        class_id
      }
    }
    getAdmissionForms(institute_id: $institution_id, branch_id: $branch_id_str) {
      id
      form_name
    }
  }
`;

const GET_FORM_LAYOUT = gql`
  query($admission_form_id: Int!, $enquiry_form_master_id: Int!) {
    getAdmissionFormToRender(admission_form_id: $admission_form_id, enquiry_form_master_id: $enquiry_form_master_id) {
      form {
        id
        form_name
      }
      fields {
        id
        field_name
        field_type_id
        field_type_name
        field_key
        options
        value_length
        is_required
        order_no
      }
      prefilled_from_enquiry
      class_id
      section_id
      admission_no
    }
  }
`;

const SUBMIT_ADMISSION_FORM = gql`
  mutation($input: SubmitAdmissionFormInput!) {
    submitAdmissionFormValues(input: $input)
  }
`;

class DynamicAdmissionModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      submitting: false,
      data: { classes: [], sections: [], academicYears: [] },
      formLayout: null,
      selection: { class_id: '', section_id: '', admission_no: '', academic_year_id: 2 },
      dynamicFormData: {},
      baseFormData: {},
      isAutoGenerate: false,
      error: ''
    };
  }

  componentDidMount() {
    if (this.props.show && this.props.student) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && this.props.student) {
      const modalJustOpened = !prevProps.show;
      const studentChanged = !prevProps.student || prevProps.student.id !== this.props.student.id;
      
      if (modalJustOpened || studentChanged) {
        this.setState({
          loading: true,
          submitting: false,
          data: { classes: [], sections: [], academicYears: [] },
          formLayout: null,
          selection: { class_id: '', section_id: '', admission_no: '', academic_year_id: 2 },
          dynamicFormData: {},
          baseFormData: {},
          isAutoGenerate: false,
          error: ''
        }, () => {
          this.fetchData();
        });
      }
    }
  }

  fetchData = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const instId = parseInt(Cookies.get('institutionid'));
      const branchId = parseInt(this.props.student.branch_id);
      const studentId = parseInt(this.props.student.id); // enquiry_form_master_id

      // 1. Fetch initial configs (classes, sections, forms)
      const result = await this.props.client.query({
        query: GET_INITIAL_DATA,
        variables: { institution_id: instId, branch_id_str: String(branchId) },
        fetchPolicy: 'network-only'
      });

      const branches = result.data.getBranches || [];
      const currentBranch = branches[0];
      const sections = currentBranch ? currentBranch.sections : [];

      const classes = result.data.getExamClasses || [];
      const academicYears = result.data.getAcademicYears || [];
      const admissionForms = result.data.getAdmissionForms || [];

      if (admissionForms.length === 0) {
        this.setState({ error: "No active Admission Form found for this institute.", loading: false });
        return;
      }

      const activeFormId = admissionForms[0].id;

      // 2. Fetch Form Layout & Prefilled Data
      const layoutResult = await this.props.client.query({
        query: GET_FORM_LAYOUT,
        variables: { admission_form_id: activeFormId, enquiry_form_master_id: studentId },
        fetchPolicy: 'network-only'
      });

      const layoutData = layoutResult.data.getAdmissionFormToRender;
      
      // Merge prefilled data into dynamicFormData
      const prefilled = layoutData.prefilled_from_enquiry || {};
      const dynamicFormData = {};
      
      layoutData.fields.forEach(f => {
        // Base fields are handled via baseFormData (keyed) — skip here so they
        // aren't also stored/submitted as custom field values. Mobile fields are
        // skipped too since student_mobile is derived from the parent phone.
        if (this.getBase(f) || isMobileBaseField(f)) return;
        // Try matching by exact name first
        if (prefilled[f.field_name]) {
          dynamicFormData[f.id] = prefilled[f.field_name];
        } else {
          // Try case-insensitive matching for common fields
          const ln = f.field_name.toLowerCase();
          if (ln.includes("father") && ln.includes("name") && this.props.student.father_name !== "—") {
             dynamicFormData[f.id] = this.props.student.father_name;
          } else if (ln.includes("father") && ln.includes("mobile") && this.props.student.father_mobile !== "—") {
             dynamicFormData[f.id] = this.props.student.father_mobile;
          } else if (ln.includes("mother") && ln.includes("name") && this.props.student.mother_name !== "—") {
             dynamicFormData[f.id] = this.props.student.mother_name;
          } else if (ln.includes("mother") && ln.includes("mobile") && this.props.student.mother_mobile !== "—") {
             dynamicFormData[f.id] = this.props.student.mother_mobile;
          } else {
             dynamicFormData[f.id] = '';
          }
        }
      });

      const baseFormData = {};
      BASE_FIELDS.forEach(bf => {
         let val = this.props.student?.[bf.key] || prefilled[bf.key] || '';
         if (val === '—' || val === null) val = '';
         baseFormData[bf.key] = val;
      });

      const isAdmitted = this.props.student.is_admitted === 1;
      const admittedSelection = isAdmitted ? {
        class_id: layoutData.class_id || this.props.student.class_id || '',
        section_id: layoutData.section_id || this.props.student.section_id || '',
        admission_no: layoutData.admission_no || this.props.student.admission_no || '',
        academic_year_id: this.props.student.academic_year_id || 2
      } : null;

      this.setState({
        data: { classes, sections, academicYears },
        formLayout: layoutData,
        dynamicFormData,
        baseFormData,
        ...(admittedSelection ? { selection: admittedSelection, isAutoGenerate: false } : {}),
        loading: false
      });

    } catch (err) {
      console.error("Error fetching admission form data:", err);
      this.setState({ error: "Failed to load admission form.", loading: false });
    }
  };

  handleFieldChange = (fieldId, value) => {
    this.setState(prev => ({
      dynamicFormData: {
        ...prev.dynamicFormData,
        [fieldId]: value
      }
    }));
  };

  handleBaseFieldChange = (key, value) => {
    this.setState(prev => ({
      baseFormData: {
        ...prev.baseFormData,
        [key]: value
      }
    }));
  };

  handleAdmit = async () => {
    const { selection, dynamicFormData, baseFormData, formLayout } = this.state;
    const { student, client, onSuccess, onHide } = this.props;

    if (!selection.class_id || !selection.section_id) {
      this.setState({ error: "Please select both class and section." });
      return;
    }
    
    if (!this.state.isAutoGenerate) {
      if (!selection.admission_no || selection.admission_no.trim() === '') {
        this.setState({ error: "Please enter the Admission Number." });
        return;
      }
    }

    // Validate dynamic fields
    const errors = [];
    formLayout.fields.forEach(f => {
      if (this.getBase(f) || isMobileBaseField(f)) return; // base/mobile handled separately
      const val = dynamicFormData[f.id];
      if (f.is_required && (!val || String(val).trim() === '')) {
        errors.push(`${f.field_name} is required.`);
      }
      if (val && f.value_length && String(val).length > f.value_length) {
        errors.push(`${f.field_name} exceeds max length ${f.value_length}.`);
      }
    });

    // Validate base fields. When the form has saved base rows, honor their saved
    // is_required + label; on legacy forms (no saved base rows) every base field is
    // required by default.
    const savedBaseFields = formLayout.fields.filter(f => this.getBase(f));
    if (savedBaseFields.length > 0) {
      savedBaseFields.forEach(f => {
        const base = this.getBase(f);
        if ((base && base.optional) || !this.baseIsRequired(f)) return;
        const val = baseFormData[base.key];
        if (!val || String(val).trim() === '') {
          errors.push(`${f.field_name || base.label} is required.`);
        }
      });
    } else {
      BASE_FIELDS.forEach(bf => {
        if (bf.optional) return;
        const val = baseFormData[bf.key];
        if (!val || String(val).trim() === '') {
          errors.push(`${bf.label} is required.`);
        }
      });
    }

    if (errors.length > 0) {
       this.setState({ error: errors.join(' | ') });
       return;
    }

    this.setState({ submitting: true, error: '' });
    
    try {
      const fieldValuesInput = Object.keys(dynamicFormData).map(key => ({
        admission_field_id: parseInt(key),
        field_value: String(dynamicFormData[key])
      }));

      const userId = parseInt(Cookies.get("id") || Cookies.get("user_id") || "0");

      // Base fields (student_name, dob, father_name, …) are sent by their stable
      // field_key so the backend writes them straight onto the edu_students row.
      const baseValues = BASE_FIELDS.map(bf => ({
        field_key: bf.key,
        field_value: String(baseFormData[bf.key] || '')
      }));

      // student_mobile is no longer collected as a basic field — store it on the
      // edu_students row using whichever parent phone is available.
      baseValues.push({
        field_key: 'student_mobile',
        field_value: String(baseFormData['father_phone'] || baseFormData['mother_phone'] || '')
      });

      // Single mutation: creates the edu_students record from base_values and stores
      // the custom (non-base) answers as admission_field_value rows.
      await client.mutate({
        mutation: SUBMIT_ADMISSION_FORM,
        variables: {
          input: {
            admission_form_id: formLayout.form.id,
            enquiry_form_master_id: parseInt(student.id),
            inserted_by: userId,
            class_id: parseInt(selection.class_id),
            section_id: parseInt(selection.section_id),
            academic_year_id: parseInt(selection.academic_year_id),
            admission_no: this.state.isAutoGenerate ? '' : selection.admission_no.trim(),
            field_values: fieldValuesInput,
            base_values: baseValues
          }
        }
      });

      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      this.setState({ error: err.message?.replace('GraphQL error: ', '') || 'Failed to admit student.', submitting: false });
    }
    this.setState({ submitting: false });
  };

  // Match a saved field name back to one of the special base fields (by label).
  // Fallback only — used for legacy rows that have no stored field_key.
  getBaseByName = (name) =>
    BASE_FIELDS.find(bf => bf.label.toLowerCase() === String(name || '').toLowerCase());

  // Match a saved field to a special base field. Prefer the stable field_key (the
  // label is admin-editable in the builder, so name matching alone is unreliable);
  // fall back to the name for legacy rows saved before keys existed.
  getBase = (f) =>
    (f && f.field_key && BASE_FIELDS.find(bf => bf.key === f.field_key)) ||
    this.getBaseByName(f && f.field_name);

  // Type-aware control shared by base and custom fields. The raw DB type label varies
  // ("Number", "Radio Button", "Date Picker"…), so classify by substring not exact match.
  // `value`/`onChange` bind to whichever store (baseFormData by key, or dynamicFormData
  // by id) the caller owns. `idKey` only needs to be unique (used for radio group names).
  renderControl = ({ idKey, typeName, options, value, valueLength, onChange }) => {
    const isLocked = this.props.student?.is_admitted === 1;
    const inputStyle = { border: `1px solid ${THEME.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "14px", color: THEME.text, width: "100%", background: isLocked ? '#f1f5f9' : '#fff' };

    let opts = [];
    if (options) {
      try {
        opts = typeof options === 'string' ? JSON.parse(options) : options;
      } catch (e) {
        opts = [];
      }
    }
    const optValue = (opt) => (opt && typeof opt === 'object' ? opt.value : opt);
    const optLabel = (opt) => (opt && typeof opt === 'object' ? opt.label : opt);

    const type = (typeName || '').toLowerCase();

    if (type.includes('dropdown') || type.includes('select')) {
      return (
        <select style={inputStyle} value={value} disabled={isLocked} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select an option</option>
          {opts.map((opt, i) => (
            <option key={i} value={optValue(opt)}>{optLabel(opt)}</option>
          ))}
        </select>
      );
    }

    if (type.includes('radio')) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', paddingTop: '4px' }}>
          {opts.map((opt, i) => {
            const ov = String(optValue(opt));
            return (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: THEME.text, marginBottom: 0 }}>
                <input type="radio" name={`field-${idKey}`} value={ov} checked={String(value) === ov} disabled={isLocked} onChange={(e) => onChange(e.target.value)} />
                {optLabel(opt)}
              </label>
            );
          })}
        </div>
      );
    }

    if (type.includes('checkbox')) {
      const selected = value ? String(value).split(',').map(s => s.trim()).filter(Boolean) : [];
      const toggle = (ov) => {
        const next = selected.includes(ov) ? selected.filter(s => s !== ov) : [...selected, ov];
        onChange(next.join(','));
      };
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', paddingTop: '4px' }}>
          {opts.map((opt, i) => {
            const ov = String(optValue(opt));
            return (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: THEME.text, marginBottom: 0 }}>
                <input type="checkbox" value={ov} checked={selected.includes(ov)} disabled={isLocked} onChange={() => toggle(ov)} />
                {optLabel(opt)}
              </label>
            );
          })}
        </div>
      );
    }

    if (type.includes('textarea')) {
      return (
        <textarea style={inputStyle} rows={2} value={value} disabled={isLocked} readOnly={isLocked} maxLength={valueLength || 500} onChange={(e) => onChange(e.target.value)} />
      );
    }

    // Single-line inputs — pick the HTML input type from the field type.
    let inputType = 'text';
    if (type.includes('date')) inputType = 'date';
    else if (type.includes('email')) inputType = 'email';
    else if (type.includes('number') || type.includes('phone') || type.includes('mobile')) inputType = 'number';

    return (
      <input
        type={inputType}
        style={inputStyle}
        value={value}
        disabled={isLocked}
        readOnly={isLocked}
        maxLength={inputType === 'number' ? undefined : (valueLength || 255)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  // Base fields are conceptually required. Honor an explicit saved is_required=false,
  // but default to required when unset (legacy forms have no saved base row).
  baseIsRequired = (field) => !(field && (field.is_required === 0 || field.is_required === false));

  // Render a base field (bound to baseFormData by its key). `field` is the saved layout
  // row, so base fields honor their API type too (e.g. Gender → dropdown, DOB → date).
  // labelOverride is the admin-edited label; falls back to the default base label.
  renderBaseInput = (bf, key, labelOverride, field) => {
    const val = this.state.baseFormData[bf.key] || '';
    // Fallback type for legacy forms where the base field row wasn't saved.
    const typeName = field ? field.field_type_name : (bf.key === 'dob' ? 'Date' : 'Text');
    const required = !bf.optional && this.baseIsRequired(field);
    // edu_students.gender is enum('Boy','Girl'); force these options regardless of
    // what the saved form stored (older forms may have Male/Female/Other).
    const options = bf.key === 'gender' ? ['Boy', 'Girl'] : (field ? field.options : null);
    return (
      <Col md={6} key={key}>
        <div className="mb-3">
          <label style={{ fontSize: "12px", fontWeight: "600", color: THEME.textMuted, marginBottom: "6px", display: "block" }}>
            {labelOverride || bf.label} {required && <span className="text-danger">*</span>}
          </label>
          {this.renderControl({
            idKey: `base-${bf.key}`,
            typeName,
            options,
            value: val,
            valueLength: field ? field.value_length : 255,
            onChange: (v) => this.handleBaseFieldChange(bf.key, v),
          })}
        </div>
      </Col>
    );
  };

  renderDynamicField = (field) => {
    const value = this.state.dynamicFormData[field.id] || '';
    const labelStyle = { fontSize: "12px", fontWeight: "600", color: THEME.textMuted, marginBottom: "6px", display: "block" };
    return (
      <div key={field.id} className="mb-3">
        <label style={labelStyle}>{field.field_name} {field.is_required && <span className="text-danger">*</span>}</label>
        {this.renderControl({
          idKey: field.id,
          typeName: field.field_type_name,
          options: field.options,
          value,
          valueLength: field.value_length,
          onChange: (v) => this.handleFieldChange(field.id, v),
        })}
      </div>
    );
  };

  render() {
    const { show, onHide, student } = this.props;
    const { loading, submitting, data, selection, formLayout, error } = this.state;
    const isLocked = student?.is_admitted === 1;
    const lockedBg = '#f1f5f9';

    // Classes are scoped to the admission form's branch: only show classes that
    // have at least one section in this branch (branch → class → section).
    const branchClassIds = new Set(data.sections.map(s => parseInt(s.class_id)));
    const branchClasses = data.classes.filter(c => branchClassIds.has(parseInt(c.id)));

    const filteredSections = selection.class_id
      ? data.sections.filter(s => parseInt(s.class_id) === parseInt(selection.class_id))
      : data.sections;

    return (
      <Modal show={show} onHide={onHide} centered size="xl">
        <Modal.Header closeButton style={ADM_MODAL_HEADER}>
          <Modal.Title style={ADM_MODAL_TITLE}>
            <span style={ADM_MODAL_HEADER_DOT} />
            <i className="fal fa-user-graduate" />
            <span>
              {isLocked ? 'Admission Details' : 'Admission Form'}
              <span style={ADM_MODAL_TITLE_SUB}>{isLocked ? 'Student already admitted — fields are read-only' : 'Fill in the admission details to complete enrollment'}</span>
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ ...ADM_MODAL_BODY, padding: '24px' }}>
          
          {/* Header Info */}
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <div>
               <h5 className="mb-1" style={{ fontWeight: '800', color: THEME.text }}>{student?.student_name}</h5>
               <p className="text-muted small mb-0">Fill out the required details to finalize admission</p>
            </div>
            {student?.student_mobile && (
              <span style={{ background: '#fff', border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', color: THEME.text }}>
                <i className="fas fa-phone-alt mr-2" style={{ color: THEME.primary }} />
                {student.student_mobile}
              </span>
            )}
          </div>

          {error && <div className="alert alert-danger py-2 small" style={{ borderRadius: '8px', fontWeight: '600' }}>⛔ {error}</div>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: THEME.primary }} />
              <p className="mt-2 text-muted small font-weight-bold">Loading admission layout...</p>
            </div>
          ) : !formLayout ? (
            <div className="text-center py-5 text-muted">No layout loaded.</div>
          ) : (
            <Row>
              {/* Left Column: Academic Allocation */}
              <Col lg={4} className="mb-4">
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: `1px solid ${THEME.border}`, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', height: '100%' }}>
                  <div className="mb-4 d-flex align-items-center">
                    <div style={{ background: "#eff6ff", color: THEME.primary, width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContext: "center", marginRight: "12px", justifyContent: "center" }}>
                      <i className="fal fa-books" style={{ fontSize: "16px" }} />
                    </div>
                    <h6 style={{ margin: 0, fontWeight: "800", color: THEME.text, fontSize: "15px" }}>Academic Allocation</h6>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "12px", fontWeight: "600", color: THEME.textMuted }}>Select Class {!isLocked && <span className="text-danger">*</span>}</Form.Label>
                    <Form.Control
                      as="select"
                      value={selection.class_id}
                      disabled={isLocked}
                      onChange={(e) => this.setState({ selection: { ...selection, class_id: e.target.value, section_id: '' } })}
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "8px", fontSize: "14px", background: isLocked ? lockedBg : '#fff' }}
                    >
                      <option value="">-- Choose Class --</option>
                      {branchClasses.map(c => (
                        <option key={c.id} value={c.id}>{c.class}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "12px", fontWeight: "600", color: THEME.textMuted }}>Select Section {!isLocked && <span className="text-danger">*</span>}</Form.Label>
                    <Form.Control
                      as="select"
                      value={selection.section_id}
                      onChange={(e) => this.setState({ selection: { ...selection, section_id: e.target.value } })}
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "8px", fontSize: "14px", background: isLocked ? lockedBg : '#fff' }}
                      disabled={isLocked || !selection.class_id}
                    >
                      <option value="">-- Choose Section --</option>
                      {filteredSections.map(s => (
                        <option key={s.id} value={s.id}>{s.section_name}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "12px", fontWeight: "600", color: THEME.textMuted }}>Academic Year</Form.Label>
                    <Form.Control
                      as="select"
                      value={selection.academic_year_id}
                      disabled={isLocked}
                      onChange={(e) => this.setState({ selection: { ...selection, academic_year_id: e.target.value } })}
                      style={{ border: `1px solid ${THEME.border}`, borderRadius: "8px", fontSize: "14px", background: isLocked ? lockedBg : '#fff' }}
                    >
                      {data.academicYears.map(ay => (
                        <option key={ay.id} value={ay.id}>{ay.academic_year}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  {!isLocked && (
                    <div style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", borderRadius: "10px", padding: "16px", border: "1px solid #bfdbfe", marginTop: "24px" }}>
                      <Form.Check
                        type="switch"
                        id="auto-generate-switch"
                        label={<span style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af' }}>Auto-generate Admission Number</span>}
                        checked={this.state.isAutoGenerate}
                        onChange={(e) => this.setState({ isAutoGenerate: e.target.checked, error: '' })}
                      />
                    </div>
                  )}

                  {(isLocked || !this.state.isAutoGenerate) && (
                    <Form.Group className="mt-3">
                      <Form.Label style={{ fontSize: "12px", fontWeight: "600", color: THEME.textMuted }}>Admission Number {!isLocked && <span className="text-danger">*</span>}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Admission Number"
                        value={selection.admission_no}
                        readOnly={isLocked}
                        disabled={isLocked}
                        onChange={(e) => this.setState({ selection: { ...selection, admission_no: e.target.value } })}
                        style={{ border: `1px solid ${THEME.border}`, borderRadius: "8px", fontSize: "14px", background: isLocked ? lockedBg : '#fff' }}
                      />
                    </Form.Group>
                  )}
                </div>
              </Col>

              {/* Right Column: Dynamic Form Fields */}
              <Col lg={8}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: `1px solid ${THEME.border}`, boxShadow: '0 2px 4px rgba(0,0,0,0.02)', height: '100%' }}>
                  <div className="mb-4 d-flex align-items-center">
                    <div style={{ background: "#ecfdf5", color: THEME.success, width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContext: "center", marginRight: "12px", justifyContent: "center" }}>
                      <i className="fal fa-file-invoice" style={{ fontSize: "16px" }} />
                    </div>
                    <h6 style={{ margin: 0, fontWeight: "800", color: THEME.text, fontSize: "15px" }}>{formLayout.form.form_name}</h6>
                  </div>

                  {formLayout.fields.length === 0 ? (
                    <div className="text-center py-4 text-muted">No fields defined for this form.</div>
                  ) : (
                    <Row>
                      {(() => {
                        const ordered = [...formLayout.fields].sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
                        const hasSavedBase = ordered.some(f => this.getBase(f));

                        // Legacy form (base fields were never saved): keep old behaviour
                        // — base fields first (in their default order), then custom.
                        if (!hasSavedBase) {
                          return (
                            <>
                              {BASE_FIELDS.map((bf, idx) => this.renderBaseInput(bf, `legacy-base-${idx}`))}
                              {ordered.filter(f => !isMobileBaseField(f)).map(f => (
                                <Col md={6} key={f.id}>{this.renderDynamicField(f)}</Col>
                              ))}
                            </>
                          );
                        }

                        // Data-driven: render every field in its saved order; base
                        // fields render as their special inputs, deduped by name.
                        return ordered.map(f => {
                          if (isMobileBaseField(f)) return null;
                          const base = this.getBase(f);
                          if (base) return this.renderBaseInput(base, `base-${f.id}`, f.field_name, f);
                          return (
                            <Col md={6} key={f.id}>{this.renderDynamicField(f)}</Col>
                          );
                        });
                      })()}
                    </Row>
                  )}
                </div>
              </Col>
            </Row>
          )}

        </Modal.Body>
        <Modal.Footer style={ADM_MODAL_FOOTER}>
          <Button variant="light" onClick={onHide} style={{ height: '40px', borderRadius: '8px', fontWeight: '600', padding: '0 24px' }}>
            {isLocked ? 'Close' : 'Cancel'}
          </Button>
          {!isLocked && (
            <Button
              variant="primary"
              onClick={this.handleAdmit}
              disabled={submitting || loading}
              style={{ background: THEME.primary, border: 'none', height: '40px', borderRadius: '8px', fontWeight: '600', padding: '0 24px' }}
            >
              {submitting ? (
                <><Spinner animation="border" size="sm" className="mr-2" /> Submitting...</>
              ) : (
                'Confirm & Admit Student'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withApollo(DynamicAdmissionModal);
